import { useCallback } from "react";
import { useDeviceProgress } from "./useDeviceProgress";

// Flat per-group counters, stored in the signed-in learner's device progress
// (synced to their account) like the other "...Counts" features: keys are
// "<groupId>:right" and "<groupId>:wrong". The "Counts" suffix is what makes the
// shared progress sanitiser keep them as numbers.
const FEATURE_KEY = "similarKanjiCounts";
export type SimilarKanjiCounts = Record<string, number>;

export interface GroupTally {
    right: number;
    wrong: number;
}

export function groupTally(counts: SimilarKanjiCounts, groupId: string): GroupTally {
    const read = (key: string) => (typeof counts[key] === "number" ? counts[key] : 0);
    return { right: read(`${groupId}:right`), wrong: read(`${groupId}:wrong`) };
}

// A pair is "often mixed up" once it has been missed at least twice and at least
// a third of its answers were wrong.
export function isOftenMixedUp(tally: GroupTally): boolean {
    const total = tally.right + tally.wrong;
    return tally.wrong >= 2 && tally.wrong / total >= 1 / 3;
}

export function useSimilarKanjiStats() {
    // Not guest-editable: stats are only kept for signed-in learners.
    const { data: counts, update, syncStatus } = useDeviceProgress<SimilarKanjiCounts>(FEATURE_KEY);
    const recordAnswer = useCallback((groupId: string, correct: boolean) => {
        void update({ kind: "increment", key: `${groupId}:${correct ? "right" : "wrong"}` });
    }, [update]);
    return { counts, recordAnswer, syncStatus, signedOut: syncStatus === "signedOut" };
}
